import React, { useCallback, useEffect, useState } from "react";
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { toast } from "react-toastify";
import { hr, STATUS_LABELS, DAY_STATUS, WEEKDAYS, monthLabel, shiftMonth, avatarColor, initials } from "./api";
import styles from "./Hrms.module.css";

// GET `path` on mount / when it changes; `reload()` re-fetches. A falsy path
// loads nothing (used by dialogs that only fetch once opened).
export function useLoad(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const reload = useCallback(async () => {
    if (!path) return;
    setLoading(true);
    setError("");
    try {
      setData((await hr(path)).data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [path]);
  useEffect(() => {
    reload();
  }, [reload]);
  return { data, loading, error, reload };
}

// Run a write, toast the server's message, then `after()`.
export function useAction() {
  const [busy, setBusy] = useState(false);
  const run = useCallback(async (path, opts, after) => {
    setBusy(true);
    try {
      const { data, msg } = await hr(path, opts);
      if (msg && msg !== "ok") toast.success(msg);
      if (after) await after(data);
      return data;
    } catch (err) {
      toast.error(err.message);
      return null;
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, run };
}

export function Loading({ error }) {
  if (error) return <div className={styles.errorBox}>{error}</div>;
  return (
    <div className={styles.card} style={{ display: "grid", placeItems: "center", minHeight: 120 }}>
      <CircularProgress size={22} />
    </div>
  );
}

// tone (good/warn/bad/info) or a colour name -> the colour class.
const TONE_COLOR = { good: "green", warn: "amber", bad: "red", info: "blue" };
export const colorClass = (c) => (c ? styles[`c_${TONE_COLOR[c] || c}`] || "" : "");

export function Card({ title, icon, color, subtitle, actions, children, className = "", style }) {
  return (
    <div className={`${styles.card} ${colorClass(color)} ${className}`} style={style}>
      {(title || actions) && (
        <div className={styles.cardHead}>
          <div style={{ minWidth: 0 }}>
            <div className={styles.cardTitle}>
              {icon && <span className={styles.cardIcon}>{icon}</span>}
              {title}
            </div>
            {subtitle && (
              <div className={styles.muted} style={{ marginTop: 4 }}>
                {subtitle}
              </div>
            )}
          </div>
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

export function Pill({ status, label }) {
  return <span className={`${styles.pill} ${styles[`pill_${status}`] || ""}`}>{label || STATUS_LABELS[status] || status}</span>;
}

export function Tag({ color, children }) {
  return <span className={`${styles.tag} ${colorClass(color)}`}>{children}</span>;
}

// Number tile: coloured side stripe + icon.
export function Stat({ label, value, tone, sub, icon, onClick }) {
  return (
    <div
      className={`${styles.tile} ${colorClass(tone)} ${onClick ? styles.tileClickable : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => e.key === "Enter" && onClick() : undefined}
    >
      {icon && <div className={styles.tileIcon}>{icon}</div>}
      <div style={{ minWidth: 0 }}>
        <div className={styles.tileValue} title={typeof value === "string" ? value : undefined}>
          {value}
        </div>
        <div className={styles.tileLabel}>{label}</div>
        {sub && <div className={styles.tileSub}>{sub}</div>}
      </div>
    </div>
  );
}

export function Avatar({ name, size = 34, className = "" }) {
  return (
    <span
      className={`${styles.avatar} ${className}`}
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38), background: avatarColor(name) }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}

export function Person({ name, sub, size = 34 }) {
  return (
    <div className={styles.person}>
      <Avatar name={name} size={size} />
      <div style={{ minWidth: 0 }}>
        <div className={styles.personName}>{name}</div>
        {sub && <div className={styles.personSub}>{sub}</div>}
      </div>
    </div>
  );
}

// Calendar-page style date: coloured month strip over the day number.
export function DateBadge({ date, color = "red" }) {
  const d = new Date(`${date}T00:00:00`);
  return (
    <div className={`${styles.dateBadge} ${colorClass(color)}`}>
      <b>{d.toLocaleDateString("en-IN", { month: "short" })}</b>
      <span>{d.getDate()}</span>
    </div>
  );
}

// Segmented control, used for sub-views and status filters.
export function Segmented({ options, value, onChange }) {
  return (
    <div className={styles.seg} role="tablist">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          aria-selected={value === o.value}
          className={value === o.value ? styles.segOn : ""}
          onClick={() => onChange(o.value)}
        >
          {o.icon}
          {o.label}
          {o.count != null && <span className={styles.segCount}>{o.count}</span>}
          {o.badge ? <span className={styles.badge}>{o.badge}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function Empty({ icon, title, text, children }) {
  return (
    <div className={styles.empty}>
      {icon && <div className={styles.emptyIcon}>{icon}</div>}
      {title && <div className={styles.emptyTitle}>{title}</div>}
      {text && <div>{text}</div>}
      {children}
    </div>
  );
}

const dialogPaper = { className: styles.tokens, sx: { borderRadius: "18px", fontFamily: '"League Spartan", "Inter", system-ui, sans-serif' } };

function DialogHead({ title, subtitle, icon, color, onClose }) {
  return (
    <div className={`${styles.cardHead} ${colorClass(color)}`} style={{ padding: "18px 20px 0", marginBottom: 6 }}>
      <div style={{ minWidth: 0 }}>
        <div className={styles.cardTitle} style={{ fontSize: 18 }}>
          {icon && <span className={styles.cardIcon}>{icon}</span>}
          {title}
        </div>
        {subtitle && (
          <div className={styles.muted} style={{ marginTop: 4 }}>
            {subtitle}
          </div>
        )}
      </div>
      {onClose && (
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
}

// A form in a dialog. `actions` are the footer buttons (Cancel is added).
export function FormDialog({ open, title, subtitle, icon, color, onClose, actions, children, maxWidth = "sm" }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth PaperProps={dialogPaper}>
      <DialogHead title={title} subtitle={subtitle} icon={icon} color={color} onClose={onClose} />
      <DialogContent sx={{ pt: "12px !important" }}>{children}</DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}>
          Cancel
        </Button>
        {actions}
      </DialogActions>
    </Dialog>
  );
}

export function ConfirmDialog({ open, title, children, confirmLabel = "Confirm", danger, onConfirm, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={dialogPaper}>
      <DialogHead title={title} />
      <DialogContent>{children}</DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={danger ? "error" : "primary"}
          onClick={onConfirm}
          sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px", boxShadow: "none" }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function MonthNav({ month, onChange }) {
  return (
    <div className={styles.calNav}>
      <button type="button" aria-label="Previous month" onClick={() => onChange(shiftMonth(month, -1))}>
        ‹
      </button>
      <span>{monthLabel(month)}</span>
      <button type="button" aria-label="Next month" onClick={() => onChange(shiftMonth(month, 1))}>
        ›
      </button>
    </div>
  );
}

export function YearNav({ year, onChange }) {
  return (
    <div className={styles.calNav}>
      <button type="button" aria-label="Previous year" onClick={() => onChange(year - 1)}>
        ‹
      </button>
      <span style={{ minWidth: 60 }}>{year}</span>
      <button type="button" aria-label="Next year" onClick={() => onChange(year + 1)}>
        ›
      </button>
    </div>
  );
}

/**
 * Month grid. `cells` maps "YYYY-MM-DD" -> { tone, lines: [string] };
 * tone is good | warn | bad | info | muted | blank.
 */
export function MonthCalendar({ month, cells, today }) {
  const [y, m] = month.split("-").map(Number);
  const first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
  const count = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const slots = [];
  for (let i = 0; i < first; i++) slots.push(null);
  for (let d = 1; d <= count; d++) slots.push(`${month}-${String(d).padStart(2, "0")}`);
  return (
    <div className={styles.cal}>
      {WEEKDAYS.map((w) => (
        <div key={w} className={styles.calDow}>
          {w}
        </div>
      ))}
      {slots.map((date, i) => {
        if (!date) return <div key={`b${i}`} className={`${styles.calCell} ${styles.calCell_blank}`} />;
        const c = cells[date] || {};
        return (
          <div
            key={date}
            className={`${styles.calCell} ${c.tone ? styles[`calCell_${c.tone}`] : ""} ${date === today ? styles.calToday : ""}`}
            title={(c.lines || []).join("\n")}
          >
            <div className={styles.calDate}>{Number(date.slice(8))}</div>
            {(c.lines || []).slice(0, 3).map((l, j) => (
              <div key={j} className={styles.calLine}>
                {l}
              </div>
            ))}
            {(c.lines || []).length > 3 && <div className={styles.calLine}>+{c.lines.length - 3} more</div>}
          </div>
        );
      })}
    </div>
  );
}

export function DayLegend() {
  const items = [
    ["#22c55e", "Present"],
    ["#f59e0b", "Late / half day / no check-out"],
    ["#ef4444", "Absent"],
    ["#3b82f6", "Leave"],
    ["#cbd5e1", "Holiday / weekly off"],
  ];
  return (
    <div className={styles.legendRow}>
      {items.map(([c, l]) => (
        <span key={l}>
          <span className={styles.legendSwatch} style={{ background: c }} />
          {l}
        </span>
      ))}
    </div>
  );
}

export const dayTone = (status) => (DAY_STATUS[status] || {}).tone || "blank";
export const dayLabel = (status) => (DAY_STATUS[status] || {}).label || "";

// One series, vertical bars with a hover tooltip; the caller also renders a
// table of the same numbers.
export function BarChart({ points, format = (v) => v }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...points.map((p) => p.value));
  return (
    <>
      <div className={styles.chart} role="img" aria-label={points.map((p) => `${p.label}: ${format(p.value)}`).join(", ")}>
        {points.map((p, i) => (
          <div key={p.label} className={styles.chartBar} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
            <span style={{ height: `${(p.value / max) * 100}%` }} />
            {hover === i && (
              <div className={styles.chartTip}>
                <strong>{p.label}</strong> · {format(p.value)}
                {p.detail ? ` · ${p.detail}` : ""}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className={styles.chartLabels}>
        {points.map((p) => (
          <span key={p.label}>{p.short || p.label}</span>
        ))}
      </div>
    </>
  );
}

export function HBars({ rows }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div>
      {rows.map((r) => (
        <div key={r.label} className={styles.hbarRow} title={`${r.label}: ${r.value}`}>
          <span className={styles.calLine}>{r.label}</span>
          <div className={styles.hbarTrack}>
            <div className={styles.hbarFill} style={{ width: `${(r.value / max) * 100}%` }} />
          </div>
          <strong style={{ textAlign: "right" }}>{r.value}</strong>
        </div>
      ))}
    </div>
  );
}
